import React, { useMemo, useState } from "react";
import {
  Box,
  Checkbox,
  IconButton,
  Pagination,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
  useTheme,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import useMediaQuery from "@mui/material/useMediaQuery";
import { Supplier } from "./types/supplier";
import { SupplierStatus } from "./enums/supplierStatus";
import { useKivunovaTranslation } from "@kivunova/kivufrontendcommon";

interface SupplierTableProps {
  suppliers: Supplier[];
  title?: string;
  onEdit?: (supplier: Supplier) => void;
  onView?: (supplier: Supplier) => void;
}

type Order = "asc" | "desc";

const SupplierTable: React.FC<SupplierTableProps> = ({
  suppliers,
  title = "Suppliers",
  onEdit,
  onView
}) => {
  const theme = useTheme();
  const matchMD = useMediaQuery(theme.breakpoints.down("md"));
  const { t } = useKivunovaTranslation();

  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [order, setOrder] = useState<Order>("asc");
  const [orderBy, setOrderBy] = useState<keyof Supplier>("name");
  const [page, setPage] = useState(1);
  const itemsPerPage = 10;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(
    null
  );

  const filteredList = useMemo(() => {
    if (!search.trim()) return suppliers;
    return suppliers.filter((supplier) =>
      Object.values(supplier)
        .join(" ")
        .toLowerCase()
        .includes(search.toLowerCase())
    );
  }, [suppliers, search]);

  const sortedList = useMemo(() => {
    return [...filteredList].sort((a, b) => {
      const aValue = a[orderBy] ?? "";
      const bValue = b[orderBy] ?? "";
      if (typeof aValue === "number" && typeof bValue === "number") {
        return order === "asc" ? aValue - bValue : bValue - aValue;
      }
      return order === "asc"
        ? String(aValue).localeCompare(String(bValue))
        : String(bValue).localeCompare(String(aValue));
    });
  }, [filteredList, order, orderBy]);

  const paginatedList = useMemo(() => {
    const start = (page - 1) * itemsPerPage;
    return sortedList.slice(start, start + itemsPerPage);
  }, [sortedList, page]);

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedIds(paginatedList.map((s) => s.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((sid) => sid !== id) : [...prev, id]
    );
  };

  const handleSort = (property: keyof Supplier) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    supplier: Supplier
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedSupplier(supplier);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedSupplier(null);
  };

  const handleEdit = () => {
    if (selectedSupplier && onEdit) {
      onEdit(selectedSupplier);
    }
    handleMenuClose();
  };

  const handleView = () => {
    if (selectedSupplier && onView) {
      onView(selectedSupplier);
    }
    handleMenuClose();
  };

  const getStatusColor = (status: SupplierStatus) => {
    switch (status) {
      case SupplierStatus.ACTIVE:
        return "success";
      case SupplierStatus.SUSPENDED:
        return "warning";
      case SupplierStatus.DELETED:
        return "error";
      default:
        return "default";
    }
  };

  const columns = matchMD
    ? [
        {
          id: "name" as keyof Supplier,
          label: t("supplier-column-name", "Name")
        }
      ]
    : [
        {
          id: "name" as keyof Supplier,
          label: t("supplier-column-name", "Name")
        },
        {
          id: "phone" as keyof Supplier,
          label: t("supplier-column-phone", "Phone")
        },
        {
          id: "email" as keyof Supplier,
          label: t("supplier-column-email", "Email")
        },
        {
          id: "status" as keyof Supplier,
          label: t("supplier-column-status", "Status")
        }
      ];

  return (
    <Box width="100%">
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
        flexDirection={matchMD ? "column" : "row"}
        gap={2}
      >
        <Typography variant="h6" fontWeight="bold">
          {title}
        </Typography>

        <TextField
          size="small"
          placeholder={t("supplier-search-placeholder", "Search suppliers")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 200 }}
        />
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  color="primary"
                  checked={
                    selectedIds.length === paginatedList.length &&
                    paginatedList.length > 0
                  }
                  indeterminate={
                    selectedIds.length > 0 &&
                    selectedIds.length < paginatedList.length
                  }
                  onChange={handleSelectAll}
                />
              </TableCell>
              {columns.map((column) => (
                <TableCell key={column.id}>
                  <TableSortLabel
                    active={orderBy === column.id}
                    direction={orderBy === column.id ? order : "asc"}
                    onClick={() => handleSort(column.id)}
                  >
                    {column.label}
                  </TableSortLabel>
                </TableCell>
              ))}
              <TableCell align="right">
                {t("supplier-column-actions", "Actions")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedList.map((supplier) => (
              <TableRow key={supplier.id} hover>
                <TableCell padding="checkbox">
                  <Checkbox
                    color="primary"
                    checked={selectedIds.includes(supplier.id)}
                    onChange={() => handleSelectOne(supplier.id)}
                  />
                </TableCell>
                <TableCell>{supplier.name}</TableCell>
                {!matchMD && (
                  <>
                    <TableCell>{supplier.phone || "-"}</TableCell>
                    <TableCell>{supplier.email || "-"}</TableCell>
                    <TableCell>
                      <Chip
                        label={supplier.status}
                        size="small"
                        color={getStatusColor(supplier.status)}
                      />
                    </TableCell>
                  </>
                )}
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={(e) => handleMenuOpen(e, supplier)}
                    sx={{
                      background:
                        anchorEl && selectedSupplier?.id === supplier.id
                          ? theme.palette.grey[300]
                          : theme.palette.grey[100],
                      transition: "all 0.2s ease",
                      "&:hover": { backgroundColor: theme.palette.grey[300] }
                    }}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {filteredList.length > itemsPerPage && (
        <Box display="flex" justifyContent="center" mt={3}>
          <Pagination
            count={Math.ceil(filteredList.length / itemsPerPage)}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
          />
        </Box>
      )}

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right"
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right"
        }}
      >
        {onView && (
          <MenuItem onClick={handleView}>
            <ListItemIcon>
              <VisibilityOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t("view", "View")}</ListItemText>
          </MenuItem>
        )}
        {onEdit && (
          <MenuItem onClick={handleEdit}>
            <ListItemIcon>
              <EditOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t("edit", "Edit")}</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
};

export default SupplierTable;
